import React, {useMemo} from "react";
import {Container} from "react-bootstrap";
import {usePage} from "@inertiajs/react";
import {createColumnHelper} from "@tanstack/react-table";
import NutzungLayout from "../../Components/Nutzung/NutzungLayout.jsx";
import useNutzungFilter from "../../Components/Nutzung/useNutzungFilter.js";
import DataGrid from "../../Components/DataGrid/DataGrid.jsx";
import {bereichLabel, formatDateTimeDE} from "../../Components/Nutzung/format.js";
import {MitarbeiterLink} from "../../Components/Nutzung/Links.jsx";
import NutzungAenderungDetails from "../../Components/Nutzung/NutzungAenderungDetails.jsx";

const spalte = createColumnHelper();

const AKTION_VARIANTE = {created: "success", updated: "primary", deleted: "danger"};
const AKTION_LABEL = {created: "Angelegt", updated: "Geändert", deleted: "Gelöscht"};

/**
 * Eigene Seite für Datenänderungen (Auftrag Teil 6) - war bisher ein Reiter
 * in Ereignisse.jsx, jetzt eigenständig unter /nutzung/aenderungen. Gleicher
 * bestehender Endpunkt /nutzung/daten/aenderungen wie zuvor, gleiche Spalten,
 * jetzt im gemeinsamen NutzungLayout-Rahmen.
 *
 * Der Name kommt vom Endpunkt zusammen mit der user_id (behobener Mangel
 * beim Abgleich der Nutzungsauswertung, siehe ActivityQuery::aenderungen) -
 * MitarbeiterLink bekommt beides, damit der Name wie überall sonst in der
 * Auswertung anklickbar ist (Kernanforderung 2). Fehlt die ID ausnahmsweise
 * (Systemereignis ohne Nutzer), zeigt MitarbeiterLink automatisch reinen
 * Text statt eines kaputten Links (siehe Links.jsx) - kein Sonderfall an
 * dieser Stelle nötig.
 */
export default function NutzungAenderungen() {
    const {nutzer, bereiche} = usePage().props;
    const nutzungFilters = useNutzungFilter();
    const {filters} = nutzungFilters;

    const mitarbeiterOptionen = useMemo(
        () => [{value: "", label: "Alle Mitarbeiter"}, ...(nutzer || []).map((u) => ({value: String(u.id), label: u.name}))],
        [nutzer]
    );
    const bereichOptionen = useMemo(
        () => [{value: "", label: "Alle Bereiche"}, ...(bereiche || []).map((b) => ({value: b, label: bereichLabel(b)}))],
        [bereiche]
    );

    const additionalFilters = useMemo(
        () => [
            {key: "von", type: "date", paramKey: "von", label: "Von", defaultValue: filters.von || ""},
            {key: "bis", type: "date", paramKey: "bis", label: "Bis", defaultValue: filters.bis || ""},
            {key: "user_id", type: "select", paramKey: "user_id", label: "Mitarbeiter", options: mitarbeiterOptionen, defaultValue: filters.user_id || ""},
            {key: "bereich", type: "select", paramKey: "bereich", label: "Bereich", options: bereichOptionen, defaultValue: filters.bereich || ""},
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [mitarbeiterOptionen, bereichOptionen]
    );

    const spalten = useMemo(
        () => [
            spalte.accessor("zeit", {
                id: "zeit",
                header: "Zeitpunkt",
                enableSorting: false,
                cell: (info) => formatDateTimeDE(info.getValue()),
                meta: {width: 150},
            }),
            spalte.accessor("nutzer", {
                id: "nutzer",
                header: "Mitarbeiter",
                enableSorting: false,
                cell: (info) => <MitarbeiterLink id={info.row.original.user_id} name={info.getValue()} />,
                meta: {width: 160, cardTitle: true},
            }),
            spalte.accessor("modell", {
                id: "modell",
                header: "Datensatz",
                enableSorting: false,
                cell: (info) => (
                    <span className="badge bg-body-secondary text-body border">
                        {info.getValue()}
                        {info.row.original.model_id ? ` #${info.row.original.model_id}` : ""}
                    </span>
                ),
                meta: {width: 150},
            }),
            spalte.accessor("aktion", {
                id: "aktion",
                header: "Aktion",
                enableSorting: false,
                cell: (info) => (
                    <span className={`badge bg-${AKTION_VARIANTE[info.getValue()] || "secondary"}`}>
                        {AKTION_LABEL[info.getValue()] || info.getValue()}
                    </span>
                ),
                meta: {width: 110},
            }),
            spalte.accessor("bezeichnung", {
                id: "bezeichnung",
                header: "Bezeichnung",
                enableSorting: false,
                cell: (info) => info.getValue() || <span className="text-muted">–</span>,
                meta: {width: 220},
            }),
            spalte.accessor("aenderungen", {
                id: "aenderungen",
                header: "Änderungen",
                enableSorting: false,
                cell: (info) => <NutzungAenderungDetails changes={info.getValue()} />,
                meta: {width: 280, help: "Vorher/Nachher je geändertem Feld - zum Aufklappen anklicken."},
            }),
        ],
        []
    );

    return (
        <Container fluid className="pt-2 pb-4">
            <NutzungLayout
                gruppe="Änderungen"
                titel="Alle Änderungen"
                nutzungFilters={nutzungFilters}
                showMitarbeiter={false}
                showBereich={false}
            >
                <p className="text-muted">
                    Jede protokollierte Änderung an beobachteten Datensätzen im gewählten Zeitraum, neueste
                    zuerst - Zeitraum, Mitarbeiter und Bereich lassen sich zusätzlich über den Filter der
                    Tabelle eingrenzen.
                </p>
                <DataGrid
                    columns={spalten}
                    getRowId={(row) => row.id}
                    initialSorting={[{id: "zeit", desc: true}]}
                    emptyLabel="Keine Datenänderungen im gewählten Zeitraum."
                    itemLabel="Änderungen"
                    itemLabelDative="Änderungen"
                    dataUrl="/nutzung/daten/aenderungen"
                    additionalFilters={additionalFilters}
                    defaultPageSize={50}
                />
            </NutzungLayout>
        </Container>
    );
}
